import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Modal, TextField, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import modalStyle from "../../styles/modalStyle";
import { textPattern } from "../../constants/patterns";
import DropDownValue from "../baseComponents/dropdownvalue";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import apiClient from "../../axiosClient";
import JWTTokenPayload from "../../interfaces/JWTTokenPayload";
import { decodedJWT, getOwnerName } from "../../utils/commonUtils";
import DispositionTree from "../../interfaces/dispositionNode";

interface AddDispositionModalProps {
    open: boolean;
    onClose: () => void;
    getAPIFunction: () => Promise<void>;
    parentID: string;
    selectedDispositionNode?: DispositionTree;
};

const styles = {
    horizontalStyle: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 },
    widthStyle: { width: '49%' },
};
const interactionModes = ['Call', 'Visit'];

const AddDispositionModal: React.FC<AddDispositionModalProps> = ({
    open, onClose, getAPIFunction, parentID, selectedDispositionNode 
}) => {
    const token: false | JWTTokenPayload = decodedJWT();
    const ownerName = getOwnerName(token);
    const picklist = useSelector((state: RootState) => state.picklists.picklists);
    const [dispositionName, setDispositionName] = useState<string>('');
    const [dispositionLabel, setDispositionLabel] = useState<string>('');
    const [interactionMode, setInteractionMode] = useState<string[]>([]);
    const [interactionType, setInteractionType] = useState<string>('None');

    useEffect(() => {
        if (selectedDispositionNode) {
            setDispositionName(selectedDispositionNode.node.disposition_name);
            setDispositionLabel(selectedDispositionNode.node.disposition_label);
            setInteractionType(selectedDispositionNode.node.interaction_type || 'None');
            setInteractionMode(selectedDispositionNode.node.interaction_mode.split(','));
        }
    }, []);

    const handleCloseModal = () => {
        setDispositionName('');
        setDispositionLabel('');
        setInteractionMode([]);
        setInteractionType('');
        onClose();
    };

    const addOrEditDisposition = async () => {
        try {
            if (!dispositionLabel || !dispositionName || !interactionMode.length) {
                alert('Please fill the necessary values');
                return;
            }
            let body: { [key: string]: any } = {
                disposition_id: selectedDispositionNode ? selectedDispositionNode.node.disposition_id : null,
                disposition_name: dispositionName,
                disposition_label: dispositionLabel,
                interaction_mode: interactionMode.join(','),
                interaction_type: interactionType !== 'None' ? interactionType : null,
                parent_id: parentID,
                is_standard: false,
                client_name: ownerName || '',
                is_active: true,
            };
            let response;
            if (selectedDispositionNode) {
                response = await apiClient.post('/api/v1/disposition', body);
            } else {
                response = await apiClient.put('/api/v1/disposition', body);
            }
            if (response.status === 200) {
                alert("Disposition added successfully");
                handleCloseModal();
                await getAPIFunction();
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
    };

    return (
        <Modal open={open} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography id="modal-title" variant="h6">
                        {parentID ? 'Add New' : selectedDispositionNode ? 'Edit' : ''} Disposition
                    </Typography>
                    <IconButton onClick={handleCloseModal}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box sx={styles.horizontalStyle}>
                    <TextField
                        label="Disposition Name"
                        value={dispositionName}
                        onChange={(e) => setDispositionName(e.target.value.replace(textPattern, ""))}
                        sx={styles.widthStyle}
                        required
                    />
                    <TextField
                        label="Disposition Label"
                        value={dispositionLabel}
                        onChange={(e) => setDispositionLabel(e.target.value.replace(textPattern, ""))}
                        sx={styles.widthStyle}
                        required
                    />
                </Box>
                <Box sx={styles.horizontalStyle}>
                    <DropDownValue
                        label="Interaction Mode"
                        items={interactionModes}
                        selectedValue={interactionMode}
                        onSelection={setInteractionMode}
                        multiple
                        style={styles.widthStyle}
                        required
                    />
                    <DropDownValue
                        label="Interaction Type"
                        selectedValue={interactionType}
                        onSelection={setInteractionType}
                        style={styles.widthStyle}
                        disabled={selectedDispositionNode ? selectedDispositionNode.children.filter(val => val.node.is_active).length > 0 : false}
                        items={[...picklist.INTERACTION_TYPE, 'None']}
                    />
                </Box>
                <Box mt={2} textAlign="right">
                    <Button variant="contained" color="primary" onClick={addOrEditDisposition}>
                        <Typography>Submit</Typography>
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddDispositionModal;